
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import { Options, Vue } from "vue-class-component";
import { State } from "@/store/helper";
import { JournalForTable } from "@/views/JournalList.vue";
import {
  getAnalysisOfJournal,
  getReflectionsOfJournal,
  setAnalysisOfJournal
} from "@/api/journal";
import { TutorMeta } from "@/api/tutor";
import {
  JournalAiAnalysis,
  journalAiAnalysisCollectionKey
} from "@/entities/journal";
import store from "@/store";
import { Student } from "@/entities/student";
import { isProd } from "@/env";
import firebase from "firebase/app";
import dayjs from "dayjs";
import Markdown from "vue3-markdown-it";
import mk from "markdown-it-katex";

const keyWord = "${journal_reflections}";
const defaultPrompt = `以下の結果を分析してください。\n\n${keyWord}`;

@Options({
  components: {
    MBaseModal,
    MButton,
    Markdown
  },
  emits: ["close"],
  props: {
    journal: Object,
    students: Array,
    prompt: {
      type: String,
      default: defaultPrompt
    }
  }
})
export default class JournalAnalysisModal extends Vue {
  @State("allStudentsFetched") allStudentsFetched?: boolean;
  @State("tutor") tutor!: TutorMeta | null;

  plugins = [{ plugin: mk }];

  students: Student[] = [];
  journal!: JournalForTable;
  initialLoading = false;
  analysisGenerating = false;
  analysis: JournalAiAnalysis | null = null;
  prompt = "";

  get analysisExists(): boolean {
    return !!this.analysis?.data.analysis;
  }

  close() {
    this.$emit("close");
  }

  async getAiResponse() {
    if (!this.tutor) return;

    if (!this.allStudentsFetched) {
      alert("すべての生徒情報が読み込まれるまでお待ちください。");
      return;
    }

    if (this.analysisExists) {
      const res = window.confirm(
        "すでに分析が存在しますが、再度取得しますか？"
      );
      if (!res) return;
    }

    try {
      this.analysisGenerating = true;
      store.commit("START_LOADING", "取得中...");

      const reflections = await getReflectionsOfJournal(
        this.journal.ref,
        this.tutor?.config
      );

      if (reflections.length === 0) {
        alert("このジャーナルには振り返りがありません。");
        return;
      }

      const reflectionItems = this.journal.journalReflectionItems.map(item => {
        const { title, type, uid } = item;
        return { title, type, uid };
      });

      const formattedReflections = [...reflections]
        .sort((a, b) => {
          // 生徒IDごと -> 日付の順でソート
          const studentIdComparison = a.data.submitterDetail.studentId.localeCompare(
            b.data.submitterDetail.studentId
          );

          if (studentIdComparison !== 0) {
            return studentIdComparison;
          }

          return a.data.createdAt - b.data.createdAt;
        })
        .map(reflection => {
          const {
            date,
            reflections: studentReflections,
            submitterDetail
          } = reflection.data;

          const reflections = reflectionItems
            .map(({ title, uid }) => {
              const found = studentReflections.find(
                _ => _.targetReflectionItemId === uid
              );
              if (found) {
                return {
                  title,
                  content: found.content
                };
              } else return null;
            })
            .filter(_ => _ !== null) as {
            title: string;
            content: string | number;
          }[];

          return {
            date,
            reflections,
            studentId: submitterDetail.studentId
          };
        })
        .reduce(
          (acc, current) => {
            const { date, reflections, studentId } = current;
            if (!acc[studentId]) {
              acc[studentId] = [];
            }
            acc[studentId].push({
              date,
              reflections
            });

            return acc;
          },
          {} as Record<
            string,
            {
              date: string;
              reflections: {
                title: string;
                content: string | number;
              }[];
            }[]
          >
        );

      const content = Object.entries(formattedReflections)
        .map(([k, v]) => {
          const name =
            this.students.find(student => student.ref.id === k)?.data.name ??
            "不明な生徒";
          return `【生徒名前】:${name}
${v
  .map(({ date, reflections }) => {
    return `【日付】: ${date}
【振り返り内容】
${reflections
  .map(r => {
    return `${r.title} : ${r.content}`;
  })
  .join("\n")}`;
  })
  .join("\n\n")}`;
        })
        .join("\n\n");

      if (!isProd) {
        console.warn("商用では表示されません");
        console.log("送信される雛形");
        console.log(this.prompt);
      }

      const call = firebase
        .app()
        .functions("asia-northeast1")
        .httpsCallable("journal_ai");

      const res = await call({
        purpose: "analysis",
        promptTemplate: this.prompt,
        content
      });

      const message = res.data?.message ?? "";

      if (!message) {
        throw new Error("Failed to get response");
      }

      const ref = this.journal.ref
        .collection(journalAiAnalysisCollectionKey)
        .doc(this.tutor.main.ref.id);

      const now = dayjs().unix();

      await setAnalysisOfJournal(
        ref,
        this.analysisExists
          ? { isUpdating: true, data: { analysis: message } }
          : {
              isUpdating: false,
              data: {
                analysis: message,
                createdAt: now
              }
            }
      );

      this.analysis = {
        ref,
        data: {
          analysis: message,
          createdAt: this.analysis ? this.analysis.data.createdAt : now
        }
      };
    } catch (e) {
      console.error(e);
      alert(
        `取得に失敗しました。\n${
          e instanceof Error ? e.message : "unknown error"
        }`
      );
    } finally {
      this.analysisGenerating = false;
      store.commit("END_LOADING");
    }
  }

  async created(): Promise<void> {
    if (!this.tutor) return;

    try {
      this.initialLoading = true;
      this.analysis = await getAnalysisOfJournal(
        this.journal.ref,
        this.tutor.main.ref.id
      );

      //TODO: promptを親からもらう(nullable)
    } catch (e) {
      console.error(e);
      alert(
        `情報の取得に失敗しました。\n${
          e instanceof Error ? e.message : "unknown error"
        }`
      );
    } finally {
      this.initialLoading = false;
    }
  }
}
